import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid2,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import TextInput from "../../components/common/input/TextInput";
import NumberInput from "../../components/common/input/NumberInput";
import withAuth from "../../hocs/withAuth";
import MainLayout from "../../components/common/layout/main/MainLayout";
import { asyncSearchList } from "../../utils/httpRequests";
import moment from "moment";
import { useSelector } from "react-redux";

// Định nghĩa kiểu dữ liệu khớp với cấu trúc gốc trả về từ API
interface BiddingItem {
  _id: string;
  tbmt: string;
  cdt: string;
  soluong: number;
  dongia: number;
  ten_thuoc?: string;
  hoat_chat?: string;
  ngayquyetdinh?: string;
}

function BiddingStatisticsPage() {
  const token = useSelector((state: any) => state.auth.token);

  // State quản lý bộ lọc
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [activeIngredient, setActiveIngredient] =
    useState<string>("Paracetamol"); // Để sẵn text mẫu để tiện test nếu cần
  const [groupBy, setGroupBy] = useState<"maTbmt" | "donViTrungThau">("maTbmt");

  // State quản lý dữ liệu gốc từ API và trạng thái
  const [data, setData] = useState<BiddingItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false); // Kiểm soát trạng thái đã ấn tìm kiếm hay chưa

  // State phân trang
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [totalRows, setTotalRows] = useState<number>(0);

  // Hàm format tiền tệ VNĐ
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(value);
  };

  // Vô hiệu hoá nút tìm kiếm nếu chưa nhập đủ thông tin
  const isSearchDisabled = loading || !year || activeIngredient.trim() === "";

  // Hàm gọi API lấy tổng số dòng và danh sách dữ liệu
  const handleSearch = async (
    currentPage = page,
    currentLimit = rowsPerPage,
  ) => {
    setLoading(true);
    setHasSearched(true); // Đánh dấu đã thực hiện tìm kiếm ít nhất 1 lần
    try {
      const startOfYear = moment().year(year).startOf("year").toISOString();
      const endOfYear = moment().year(year).endOf("year").toISOString();

      const queryConditions: any = {
        ngayquyetdinh: {
          $gte: startOfYear,
          $lte: endOfYear,
        },
      };

      if (activeIngredient.trim() !== "") {
        queryConditions.hoat_chat = { $regex: activeIngredient, $options: "i" };
      }

      // Gọi song song API đếm tổng số dòng (count: 1) và API lấy data phẳng
      const [countResp, dataResp] = await Promise.all([
        asyncSearchList({
          apiCode: "cr_thuoc",
          token,
          withIdApp: true,
          applyToken: true,
          condition: {
            count: 1,
            q: queryConditions,
          },
        }),
        asyncSearchList({
          apiCode: "cr_thuoc",
          token,
          withIdApp: true,
          applyToken: true,
          condition: {
            page: currentPage + 1,
            limit: currentLimit,
            q: queryConditions,
          },
        }),
      ]);

      // Cập nhật tổng số dòng
      if (countResp && countResp.data) {
        setTotalRows(countResp.data.rows_number || 0);
      }

      // Gán dữ liệu hiển thị phẳng từ API
      if (dataResp && dataResp.data) {
        setData(dataResp.data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Lỗi khi fetch dữ liệu thống kê:", error);
      setData([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý khi click chuyển trang
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    handleSearch(newPage, rowsPerPage);
  };

  // Xử lý khi thay đổi số lượng dòng hiển thị trên một trang
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    handleSearch(0, newLimit);
  };

  return (
    <MainLayout>
      <Box sx={{ maxWidth: 1200, margin: "0 auto" }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
          Báo Cáo Thống Kê Đấu Thầu Thuốc Theo Hoạt Chất
        </Typography>

        {/* --- KHU VỰC FILTER --- */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Grid2 container spacing={3} alignItems="center">
            {/* Nhập Năm */}
            <Grid2 size={{ xs: 2 }}>
              <NumberInput
                label="Năm"
                min={2000}
                id="filter_year"
                labelWidth="25%"
                name="filter_year"
                direction="column"
                value={year}
                onChange={(e: any) => setYear(+e.target.value)}
              />
            </Grid2>

            {/* Nhập Hoạt chất */}
            <Grid2 size={{ xs: 4 }}>
              <TextInput
                label="Tên hoạt chất"
                id="filter_hoatchat"
                labelWidth="25%"
                name="filter_hoatchat"
                value={activeIngredient}
                onChange={(e: any) => setActiveIngredient(e.target.value)}
              />
            </Grid2>

            {/* Radio chọn hiển thị Cột thứ nhất */}
            <Grid2 size={{ xs: 4 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontSize: "0.875rem" }}>
                  Hiển thị cột 1 theo:
                </FormLabel>
                <RadioGroup
                  row
                  value={groupBy}
                  onChange={(e) =>
                    setGroupBy(e.target.value as "maTbmt" | "donViTrungThau")
                  }
                >
                  <FormControlLabel
                    value="maTbmt"
                    control={<Radio size="small" />}
                    label="Mã TBMT"
                  />
                  <FormControlLabel
                    value="donViTrungThau"
                    control={<Radio size="small" />}
                    label="Đơn vị trúng thầu"
                  />
                </RadioGroup>
              </FormControl>
            </Grid2>

            {/* Nút Tìm kiếm */}
            <Grid2 size={{ xs: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setPage(0);
                  handleSearch(0, rowsPerPage);
                }}
                disabled={isSearchDisabled}
                sx={{ minWidth: 120, color: "white" }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Thống kê"
                )}
              </Button>
            </Grid2>
          </Grid2>
        </Paper>

        {/* --- KHU VỰC KẾT QUẢ --- */}
        <Paper elevation={2}>
          {!hasSearched ? (
            // TRẠNG THÁI 1: Vừa vào trang, chưa thực hiện thống kê
            <Box sx={{ p: 6, textAlign: "center", backgroundColor: "#fafafa" }}>
              <Typography
                variant="h6"
                color="primary"
                gutterBottom
                sx={{ fontWeight: 500 }}
              >
                Sẵn sàng kết xuất báo cáo thống kê
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Vui lòng điền đầy đủ thông tin bộ lọc bao gồm{" "}
                <strong>Năm thống kê</strong> và <strong>Tên hoạt chất</strong>,
                sau đó nhấn nút <strong>Thống kê</strong> để tải dữ liệu báo
                cáo.
              </Typography>
            </Box>
          ) : !loading && data.length === 0 ? (
            // TRẠNG THÁI 2: Đã bấm thống kê nhưng không có dữ liệu trả về từ API
            <Box sx={{ p: 6, textAlign: "center" }}>
              <Typography
                variant="h6"
                color="error"
                gutterBottom
                sx={{ fontWeight: 500 }}
              >
                Không tìm thấy dữ liệu phù hợp!
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Không ghi nhận dữ liệu đấu thầu nào cho hoạt chất "
                <strong>{activeIngredient}</strong>" trong năm{" "}
                <strong>{year}</strong>. Vui lòng kiểm tra lại từ khóa hoặc thay
                đổi tiêu chí tìm kiếm.
              </Typography>
            </Box>
          ) : (
            // TRẠNG THÁI 3: Có dữ liệu (Hoặc đang trong quá trình tải lại trang mới)
            <>
              {/* Chiều cao động dựa vào màn hình: trừ đi khoảng 360px của layout xung quanh */}
              <TableContainer
                sx={{ maxHeight: "calc(100vh - 330px)", overflowY: "auto" }}
              >
                <Table
                  stickyHeader
                  sx={{ minWidth: 650 }}
                  aria-label="bidding statistics table"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "40%",
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        {groupBy === "maTbmt" ? "Mã TBMT" : "Đơn vị trúng thầu"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                      >
                        Đơn giá
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                      >
                        Số lượng
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                      >
                        Thành tiền
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                          <CircularProgress size={30} />
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ mt: 1 }}
                          >
                            Đang cập nhật danh sách...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.map((row) => (
                        <TableRow
                          key={row._id}
                          hover
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          {/* Cột 1: Ánh xạ linh hoạt theo radio lựa chọn */}
                          <TableCell component="th" scope="row">
                            {(groupBy === "maTbmt" ? row.tbmt : row.cdt) ||
                              "Chưa xác định"}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.dongia || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat("vi-VN").format(
                              row.soluong || 0,
                            )}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 500, color: "error.main" }}
                          >
                            {formatCurrency(
                              (row.soluong || 0) * (row.dongia || 0),
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Điều khiển phân trang dưới chân bảng */}
              <TablePagination
                rowsPerPageOptions={[20, 50, 100]}
                component="div"
                count={totalRows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Số dòng mỗi trang:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} trên ${count !== -1 ? count : `hơn ${to}`}`
                }
              />
            </>
          )}
        </Paper>
      </Box>
    </MainLayout>
  );
}

export default withAuth(BiddingStatisticsPage);
